import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getOptionsEstablishment, selectOptionsEstablishment, selectOptionsEstablishmentState } from '../../../store/entities/optionsEstablishment';
import { FetchState } from '../../../store/fetchState';
import { selectIdentityCompanyId } from '../../../store/entities/identity';
import { QcsAutocomplete } from './QcsAutocomplete';
import axios, { CancelToken } from 'axios';
import { SxProps, Theme } from '@mui/material';
import { getLangNameObj } from '../../../utils/format';
import { useTranslation } from 'react-i18next';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { useFormikContext } from 'formik';
import { doNothing } from '../../../utils/shared';

interface Props {
  sx?: SxProps<Theme>;
  multiple?: boolean;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  selectFirst?: boolean;
  showEmptyOption?: boolean;
  emptyOptionLabel?: string;
  hideOnSingleOption?: boolean;
  /** Static list of establishments — bypasses Redux entirely when provided. */
  establishments?: ReferenceDto[];
  onChange?: (value: ReferenceDto | ReferenceDto[] | null) => void;
}

export const EstablishmentAutocomplete: FC<Props> = ({
  sx,
  multiple,
  name,
  label,
  disabled,
  selectFirst,
  required,
  showEmptyOption,
  emptyOptionLabel,
  hideOnSingleOption = true,
  establishments,
  onChange,
}) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const optionsEstablishment = useAppSelector(selectOptionsEstablishment);
  const optionsEstablishmentState = useAppSelector(selectOptionsEstablishmentState);
  const { setFieldValue } = useFormikContext<any>();
  // The user's only establishment, from the initial unfiltered load; null when they have several.
  // Only that load may set it — search results are filtered, so a single match there says nothing
  // about how many establishments the user has.
  const [loadedSingleEstablishment, setLoadedSingleEstablishment] = useState<ReferenceDto | null>(null);
  const autoSelectedIdRef = useRef<string | null>(null);

  // Redux path: load options when no static list is provided
  useEffect(() => {
    if (establishments !== undefined) return;
    const source = axios.CancelToken.source();
    dispatch(getOptionsEstablishment(undefined, source.token)).then((data) => {
      // Undefined means cancelled or failed — keep the previous result.
      if (!data) return;
      if (data.length !== 1) {
        setLoadedSingleEstablishment(null);
        return;
      }
      const only = data[0];
      setLoadedSingleEstablishment({ id: only.id, name: only.name, nameEn: only.nameEn });
    });
    return () => source.cancel();
  }, [dispatch, identityCompanyId]);

  const staticSingleEstablishment = establishments?.length === 1 ? establishments[0] : null;
  const singleEstablishment = establishments === undefined ? loadedSingleEstablishment : staticSingleEstablishment;
  const isSingleOption = singleEstablishment !== null;

  // Auto-select the only establishment the user has
  useEffect(() => {
    if (!singleEstablishment || autoSelectedIdRef.current === singleEstablishment.id) return;

    autoSelectedIdRef.current = singleEstablishment.id;
    const value = multiple ? [singleEstablishment] : singleEstablishment;
    setFieldValue(name, value);
    onChange?.(value);
  }, [singleEstablishment, multiple, name, setFieldValue, onChange]);

  const loadOptions = useCallback(
    (search: string | undefined, cancelToken: CancelToken) => {
      dispatch(getOptionsEstablishment(search, cancelToken));
    },
    [dispatch, identityCompanyId],
  );

  const reduxOptions = useMemo(() => {
    return showEmptyOption
      ? [
          {
            id: '',
            name: emptyOptionLabel ?? i18n.t('common.showAll'),
          },
          ...optionsEstablishment,
        ]
      : optionsEstablishment;
  }, [showEmptyOption, emptyOptionLabel, i18n, optionsEstablishment]);

  if (isSingleOption && hideOnSingleOption) {
    return null;
  }

  if (establishments !== undefined) {
    return (
      <QcsAutocomplete
        sx={sx}
        multiple={multiple}
        name={name}
        label={label}
        required={required}
        selectFirst={selectFirst}
        disabled={disabled || isSingleOption}
        options={establishments}
        loading={false}
        loadingError={false}
        loadOptions={doNothing}
        onChange={onChange}
        getOptionId={(option: ReferenceDto) => option?.id ?? ''}
        getOptionName={(option: ReferenceDto) => getLangNameObj(i18n, option)}
      />
    );
  }

  return (
    <QcsAutocomplete
      sx={sx}
      multiple={multiple}
      name={name}
      label={label}
      labelWhenEmpty={emptyOptionLabel ? emptyOptionLabel : undefined}
      required={required}
      selectFirst={selectFirst}
      disabled={disabled || isSingleOption}
      options={reduxOptions}
      loading={optionsEstablishmentState === FetchState.Loading}
      loadingError={optionsEstablishmentState === FetchState.Error}
      loadOptions={loadOptions}
      onChange={onChange}
      getOptionId={(option: ReferenceDto) => option?.id ?? ''}
      getOptionName={(option: ReferenceDto) => getLangNameObj(i18n, option)}
      getValueIfNotFound={(option: ReferenceDto) => {
        return {
          id: '',
          name: emptyOptionLabel ?? i18n.t('common.showAll'),
        };
      }}
    />
  );
};
